import React, { useEffect } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import { Button, Input, InputNumber, Modal, Radio, Select, Switch } from 'antd'
import Loader from '../../pages/pages/loader'
import {
  useCreateCollectorScheduleMutation,
  useUpdateCollectorScheduleByIdMutation
} from '../../redux/apis/utilsManagement/collectorSchedule/uMCollectorScheduleApiSlice'

export const PERIODS = ['days', 'hours', 'minutes', 'seconds', 'microseconds']
export const CRON_FIELDS = ['minute', 'hour', 'day_of_week', 'day_of_month', 'month_of_year']

const jsonField = (label, kind) =>
  Yup.string()
    .required(`Please Enter ${label}`)
    .test('json', `${label} must be a JSON ${kind}`, v => {
      try {
        const parsed = JSON.parse(v)
        return kind === 'list'
          ? Array.isArray(parsed)
          : parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
      } catch {
        return false
      }
    })

const EditCreateCollectorSchedule = ({ state, open, setOpen, record, tasks = [] }) => {
  const [
    addSchedule,
    { isLoading: isAdding, isSuccess: SuccessAdd, error: errorAdd }
  ] = useCreateCollectorScheduleMutation()
  const [
    updateSchedule,
    { isLoading: isUpdating, isSuccess: SuccessUpdate, error: errorUpdate }
  ] = useUpdateCollectorScheduleByIdMutation()

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: record?.name || '',
      task: record?.task || '',
      enabled: record?.enabled ?? true,
      schedule_type: record?.interval ? 'interval' : 'crontab',
      minute: record?.crontab?.minute || '*',
      hour: record?.crontab?.hour || '*',
      day_of_week: record?.crontab?.day_of_week || '*',
      day_of_month: record?.crontab?.day_of_month || '*',
      month_of_year: record?.crontab?.month_of_year || '*',
      every: record?.interval?.every || 1,
      period: record?.interval?.period || 'hours',
      args: record?.args || '[]',
      kwargs: record?.kwargs || '{}'
    },

    validationSchema: Yup.object({
      name: Yup.string().required('Please Enter Task Name'),
      task: Yup.string().required('Please Select A Task'),
      args: jsonField('args', 'list'),
      kwargs: jsonField('kwargs', 'object')
    }),

    onSubmit: async values => {
      const body = {
        name: values.name,
        task: values.task,
        enabled: values.enabled,
        args: values.args,
        kwargs: values.kwargs
      }
      // schedule_type is create-only; on edit we patch the fields of the existing schedule
      if (state === 'create') body.schedule_type = values.schedule_type
      if (values.schedule_type === 'crontab') CRON_FIELDS.forEach(f => (body[f] = values[f]))
      else Object.assign(body, { every: Number(values.every), period: values.period })

      try {
        if (state === 'edit') {
          await updateSchedule({ id: record?.id, body }).unwrap()
        } else if (state === 'create') {
          await addSchedule(body).unwrap()
        }
      } catch (err) {
        console.error(err)
      }
    }
  })

  useEffect(() => {
    if (errorAdd?.data) {
      const errorMessages = Object.values(errorAdd.data).flat()
      errorMessages.forEach(message => {
        toast.error(String(message))
      })
    } else if (errorUpdate?.data) {
      const errorMessages = Object.values(errorUpdate.data).flat()
      errorMessages.forEach(message => {
        toast.error(String(message))
      })
    }
  }, [errorAdd, errorUpdate])

  useEffect(() => {
    if (SuccessAdd && state === 'create') {
      toast.success('Periodic Task created successfully!')
      setOpen(false)
    } else if (SuccessUpdate && state === 'edit') {
      toast.success('Periodic Task updated successfully!')
      setOpen(false)
    }
  }, [state, SuccessAdd, SuccessUpdate, setOpen])

  const field = (name, label, node) => (
    <div className='mb-3'>
      <label className='form-label'>{label}</label>
      {node}
      {validation.touched[name] && validation.errors[name] ? (
        <p className='text-danger'>{validation.errors[name]}</p>
      ) : null}
    </div>
  )

  return (
    <Modal
      className='customModal'
      open={open}
      width={700}
      title={state === 'edit' ? 'Edit Periodic Task' : 'Create Periodic Task'}
      onCancel={() => setOpen(false)}
      footer={[
        <Button key='back' onClick={() => setOpen(false)}>
          Cancel
        </Button>,
        <Button
          key='submit'
          type='primary'
          onClick={() => {
            if (Object.keys(validation.errors).length !== 0) {
              Object.keys(validation.errors).forEach(key => {
                toast.error(validation.errors[key])
              })
              return
            }
            validation.handleSubmit()
          }}
        >
          Submit
        </Button>
      ]}
    >
      <Loader isLoading={isAdding || isUpdating} />

      {field(
        'name',
        'Name',
        <Input
          type='text'
          name='name'
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.name}
        />
      )}

      {field(
        'task',
        'Task',
        <Select
          className='w-100'
          showSearch
          placeholder='Select a registered celery task'
          options={tasks.map(t => ({ value: t, label: t }))}
          value={validation.values.task || undefined}
          onChange={value => validation.setFieldValue('task', value)}
        />
      )}

      <div className='mb-3'>
        <label className='form-label me-2'>Enabled</label>
        <Switch
          checked={validation.values.enabled}
          onChange={value => validation.setFieldValue('enabled', value)}
        />
      </div>

      <div className='mb-3'>
        <label className='form-label d-block'>Schedule Type</label>
        <Radio.Group
          disabled={state === 'edit'}
          value={validation.values.schedule_type}
          onChange={e => validation.setFieldValue('schedule_type', e.target.value)}
          options={[
            { value: 'crontab', label: 'Crontab' },
            { value: 'interval', label: 'Interval' }
          ]}
        />
      </div>

      {validation.values.schedule_type === 'crontab' ? (
        <div className='row'>
          {CRON_FIELDS.map(f => (
            <div className='col-md-4' key={f}>
              {field(
                f,
                f.replace(/_/g, ' '),
                <Input
                  type='text'
                  name={f}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values[f]}
                />
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className='row'>
          <div className='col-md-6'>
            {field(
              'every',
              'Every',
              <InputNumber
                className='w-100'
                min={1}
                value={validation.values.every}
                onChange={value => validation.setFieldValue('every', value)}
              />
            )}
          </div>
          <div className='col-md-6'>
            {field(
              'period',
              'Period',
              <Select
                className='w-100'
                options={PERIODS.map(p => ({ value: p, label: p }))}
                value={validation.values.period}
                onChange={value => validation.setFieldValue('period', value)}
              />
            )}
          </div>
        </div>
      )}

      {field(
        'args',
        'Args (JSON list)',
        <Input.TextArea
          rows={2}
          name='args'
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.args}
        />
      )}

      {field(
        'kwargs',
        'Kwargs (JSON object)',
        <Input.TextArea
          rows={3}
          name='kwargs'
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.kwargs}
        />
      )}
    </Modal>
  )
}

export default EditCreateCollectorSchedule
